import React, { useState, useEffect } from "react";
import {
  Typography,
  Button,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  message,
  Space,
  Card,
  List,
  Avatar,
  Dropdown,
  Menu,
  Radio,
  Row,
  Col,
  Drawer,
  Grid,
} from "antd";
import axios from "axios";
import moment from "moment";
import locale from "antd/es/date-picker/locale/de_DE";
import TitleComponent from "../components/TitleComponent";
import LogoUploadComponent from "../components/LogoUploadComponent";
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  SearchOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
  FileImageOutlined,
  DownOutlined,
  MenuOutlined,
  EllipsisOutlined,
} from "@ant-design/icons";

const { Title, Text, Paragraph } = Typography;
const { Search } = Input;
const { useBreakpoint } = Grid;

// Konfigurationen für Dropdowns
const countryOptions = [
  { value: "DE", label: "Deutschland - DE" },
  { value: "CH", label: "Schweiz - CH" },
  { value: "AT", label: "Österreich - AT" },
  { value: "FR", label: "Frankreich - FR" },
  { value: "IT", label: "Italien - IT" },
  { value: "ES", label: "Spanien - ES" },
  { value: "NL", label: "Niederlande - NL" },
  { value: "BE", label: "Belgien - BE" },
  { value: "LU", label: "Luxemburg - LU" },
  { value: "PL", label: "Polen - PL" },
  { value: "CZ", label: "Tschechien - CZ" },
  { value: "TR", label: "Türkei - TR" },
  { value: "GB", label: "Großbritannien - GB" },
  { value: "US", label: "USA - US" },
  { value: "CN", label: "China - CN" },
];

// Optionen für die Telefonvorwahl.
const phoneCodeOptions = [
  { value: "+49", label: "Deutschland (+49)" },
  { value: "+41", label: "Schweiz (+41)" },
  { value: "+43", label: "Österreich (+43)" },
  { value: "+33", label: "Frankreich (+33)" },
  { value: "+39", label: "Italien (+39)" },
  { value: "+34", label: "Spanien (+34)" },
  { value: "+31", label: "Niederlande (+31)" },
  { value: "+32", label: "Belgien (+32)" },
  { value: "+352", label: "Luxemburg (+352)" },
  { value: "+48", label: "Polen (+48)" },
  { value: "+420", label: "Tschechien (+420)" },
  { value: "+90", label: "Türkei (+90)" },
  { value: "+44", label: "Großbritannien (+44)" },
  { value: "+1", label: "USA/Kanada (+1)" },
  { value: "+86", label: "China (+86)" },
];

const Companies = () => {
  const screens = useBreakpoint();
  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [form] = Form.useForm();

  // Feststellen, ob wir uns im mobilen Ansichtsmodus befinden
  const isMobile = !screens.md;

  // Daten laden
  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/companies`
      );
      const fetchedCompanies = response.data.companies || [];
      setCompanies(fetchedCompanies);
      sortAndFilterCompanies(
        fetchedCompanies,
        searchText,
        sortField,
        sortDirection
      );
    } catch (error) {
      console.error("Fehler beim Laden der Unternehmen:", error);
      message.error("Unternehmen konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };

  // Sortierung und Filterung der Unternehmen
  const sortAndFilterCompanies = (data, search, field, direction) => {
    let result = [...data];

    // Filtern
    if (search) {
      const lowerSearch = search.toLowerCase();
      result = result.filter(
        (company) =>
          company.name.toLowerCase().includes(lowerSearch) ||
          company.city.toLowerCase().includes(lowerSearch) ||
          company.email.toLowerCase().includes(lowerSearch)
      );
    }

    // Sortieren
    result.sort((a, b) => {
      let comparison = 0;

      switch (field) {
        case "name":
          comparison = a.name.localeCompare(b.name);
          break;
        case "city":
          comparison = a.city.localeCompare(b.city);
          break;
        case "country":
          comparison = a.country.localeCompare(b.country);
          break;
        default:
          comparison = a.name.localeCompare(b.name);
      }

      return direction === "asc" ? comparison : -comparison;
    });

    setFilteredCompanies(result);
  };

  // Logo URL für das Unternehmen generieren
  const getCompanyLogoUrl = (logoFilename) => {
    if (!logoFilename) return null;
    return `${process.env.REACT_APP_API_URL}/logo/${encodeURIComponent(
      logoFilename
    )}`;
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  // Bei Änderung der Suchkriterien oder Sortierung
  useEffect(() => {
    sortAndFilterCompanies(companies, searchText, sortField, sortDirection);
  }, [searchText, sortField, sortDirection, companies]);

  // Formular öffnen (Modal auf Desktop, Drawer auf Mobilgeräten)
  const showCreateForm = () => {
    setEditingCompany(null);
    form.resetFields();

    if (isMobile) {
      setIsDrawerVisible(true);
    } else {
      setIsModalVisible(true);
    }
  };

  // Formular zum Bearbeiten öffnen (Modal auf Desktop, Drawer auf Mobilgeräten)
  const showEditForm = (company) => {
    setEditingCompany(company);
    form.setFieldsValue({
      ...company,
      founding_date: company.founding_date
        ? moment(company.founding_date)
        : null,
    });

    if (isMobile) {
      setIsDrawerVisible(true);
    } else {
      setIsModalVisible(true);
    }
  };

  // Formular schließen
  const handleCancel = () => {
    setIsModalVisible(false);
    setIsDrawerVisible(false);
  };

  // Formular absenden
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      // Datum formatieren
      if (values.founding_date) {
        values.founding_date = values.founding_date.format("YYYY-MM-DD");
      }

      if (editingCompany) {
        // Unternehmen aktualisieren
        await axios.put(
          `${process.env.REACT_APP_API_URL}/companies/${editingCompany.id}`,
          values
        );
        message.success("Unternehmen erfolgreich aktualisiert");
      } else {
        // Neues Unternehmen anlegen
        await axios.post(`${process.env.REACT_APP_API_URL}/companies`, values);
        message.success("Unternehmen erfolgreich angelegt");
      }

      setIsModalVisible(false);
      setIsDrawerVisible(false);
      fetchCompanies(); // Liste aktualisieren
    } catch (error) {
      console.error("Fehler beim Speichern:", error);
      message.error(
        error.response?.data?.error || "Fehler beim Speichern des Unternehmens"
      );
    }
  };

  // Unternehmen löschen
  const handleDelete = async () => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/companies/${companyToDelete.id}`
      );
      message.success("Unternehmen erfolgreich gelöscht");
      setDeleteModalVisible(false);
      fetchCompanies(); // Liste aktualisieren
    } catch (error) {
      console.error("Fehler beim Löschen:", error);
      message.error(
        error.response?.data?.error || "Fehler beim Löschen des Unternehmens"
      );
      setDeleteModalVisible(false);
    }
  };

  // Menü für die Sortierung
  const sortMenu = (
    <Menu>
      <Menu.Item
        key="name_asc"
        onClick={() => {
          setSortField("name");
          setSortDirection("asc");
        }}
      >
        <SortAscendingOutlined /> Name (A-Z)
      </Menu.Item>
      <Menu.Item
        key="name_desc"
        onClick={() => {
          setSortField("name");
          setSortDirection("desc");
        }}
      >
        <SortDescendingOutlined /> Name (Z-A)
      </Menu.Item>
      <Menu.Item
        key="city_asc"
        onClick={() => {
          setSortField("city");
          setSortDirection("asc");
        }}
      >
        <SortAscendingOutlined /> Stadt (A-Z)
      </Menu.Item>
      <Menu.Item
        key="city_desc"
        onClick={() => {
          setSortField("city");
          setSortDirection("desc");
        }}
      >
        <SortDescendingOutlined /> Stadt (Z-A)
      </Menu.Item>
      <Menu.Item
        key="country_asc"
        onClick={() => {
          setSortField("country");
          setSortDirection("asc");
        }}
      >
        <SortAscendingOutlined /> Land (A-Z)
      </Menu.Item>
      <Menu.Item
        key="country_desc"
        onClick={() => {
          setSortField("country");
          setSortDirection("desc");
        }}
      >
        <SortDescendingOutlined /> Land (Z-A)
      </Menu.Item>
    </Menu>
  );

  // Aktionsmenü für mobile Version
  const mobileActionMenu = (company) => (
    <Menu>
      <Menu.Item key="edit" onClick={() => showEditForm(company)}>
        <EditOutlined /> Bearbeiten
      </Menu.Item>
      <Menu.Item
        key="delete"
        danger
        onClick={() => {
          setCompanyToDelete(company);
          setDeleteModalVisible(true);
        }}
      >
        <DeleteOutlined /> Löschen
      </Menu.Item>
    </Menu>
  );

  // Rendering des Adressteils - optimiert für mobile Darstellung
  const renderAddress = (company) => (
    <Paragraph
      ellipsis={{ rows: isMobile ? 1 : 2 }}
      style={{ margin: 0, fontSize: isMobile ? "12px" : "14px" }}
    >
      <Text type="secondary">Adresse:</Text> {company.street},{" "}
      {company.postal_code} {company.city}, {company.country}
    </Paragraph>
  );

  // Rendering des Kontaktteils - optimiert für mobile Darstellung
  const renderContact = (company) => (
    <Paragraph
      ellipsis={{ rows: isMobile ? 1 : 2 }}
      style={{ margin: 0, fontSize: isMobile ? "12px" : "14px" }}
    >
      <Text type="secondary">Kontakt:</Text> {company.phone_code}{" "}
      {company.phone_number}
      {isMobile ? " • " : <br />}
      {company.email}
    </Paragraph>
  );

  // Formular-Komponente für Modal und Drawer
  const CompanyForm = () => (
    <Form form={form} layout="vertical">
      <Form.Item
        label="Firmenlogo"
        name="logo_filename"
        tooltip="Wählen Sie ein Firmenlogo aus (JPEG, max. 5MB)"
      >
        {form.getFieldValue("logo_filename") && (
          <div style={{ marginBottom: "10px" }}>
            <img
              src={getCompanyLogoUrl(form.getFieldValue("logo_filename"))}
              alt="Vorschau"
              style={{
                maxHeight: "80px",
                maxWidth: "100%",
                border: "1px solid #d9d9d9",
                padding: "4px",
                borderRadius: "2px",
              }}
            />
          </div>
        )}
        <LogoUploadComponent />
      </Form.Item>

      <Form.Item
        label="Firmenname"
        name="name"
        rules={[{ required: true, message: "Bitte Firmennamen eingeben!" }]}
      >
        <Input placeholder="Mustermann GmbH" />
      </Form.Item>

      <Form.Item
        label="Straße & Hausnummer"
        name="street"
        rules={[{ required: true, message: "Bitte Straße eingeben!" }]}
      >
        <Input placeholder="Musterstraße 1" />
      </Form.Item>

      <Row gutter={16}>
        <Col span={isMobile ? 24 : 12}>
          <Form.Item
            label="Stadt"
            name="city"
            rules={[{ required: true, message: "Bitte Stadt eingeben!" }]}
          >
            <Input placeholder="Musterstadt" />
          </Form.Item>
        </Col>

        <Col span={isMobile ? 24 : 12}>
          <Form.Item
            label="Postleitzahl"
            name="postal_code"
            rules={[{ required: true, message: "Bitte PLZ eingeben!" }]}
          >
            <Input placeholder="12345" />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        label="Land"
        name="country"
        rules={[{ required: true, message: "Bitte Land auswählen!" }]}
      >
        <Select
          placeholder="Land auswählen"
          showSearch
          optionFilterProp="children"
          filterOption={(input, option) =>
            option.children.toLowerCase().includes(input.toLowerCase())
          }
        >
          {countryOptions.map((country) => (
            <Select.Option key={country.value} value={country.value}>
              {country.label}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Row gutter={16}>
        <Col span={isMobile ? 24 : 8}>
          <Form.Item
            label="Telefon-Vorwahl"
            name="phone_code"
            rules={[{ required: true, message: "Bitte Vorwahl auswählen!" }]}
          >
            <Select
              placeholder="Vorwahl"
              showSearch
              optionFilterProp="children"
            >
              {phoneCodeOptions.map((option) => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        <Col span={isMobile ? 24 : 16}>
          <Form.Item
            label="Telefonnummer"
            name="phone_number"
            rules={[
              { required: true, message: "Bitte Telefonnummer eingeben!" },
            ]}
          >
            <Input placeholder="123456789" />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        label="E-Mail"
        name="email"
        rules={[
          { required: true, message: "Bitte E-Mail-Adresse eingeben!" },
          {
            type: "email",
            message: "Bitte geben Sie eine gültige E-Mail-Adresse ein!",
          },
        ]}
      >
        <Input placeholder="info@mustermann.de" />
      </Form.Item>

      <Form.Item
        label="Geschäftsführer/-in (Kommagetrennt)"
        name="executives"
        rules={[
          {
            required: true,
            message: "Bitte Geschäftsführer/-innen eingeben!",
          },
        ]}
      >
        <Input placeholder="Herr Max Mustermann, Frau Erika Musterfrau" />
      </Form.Item>

      <Form.Item
        label="Gründungsdatum des Unternehmens"
        name="founding_date"
        rules={[{ required: true, message: "Bitte Gründungsdatum auswählen!" }]}
      >
        <DatePicker
          style={{ width: "100%" }}
          format="DD.MM.YYYY"
          locale={locale}
        />
      </Form.Item>
    </Form>
  );

  return (
    <Space
      direction="vertical"
      style={{
        width: "100%",
        padding: isMobile ? "12px 8px" : "24px",
        margin: "0 auto",
      }}
      size={isMobile ? "middle" : "large"}
    >
      <TitleComponent
        title="Firmendaten"
        subTitle="Erstellen und verwalten Sie Firmenvorlagen für den Dokumenten-Generator."
        breadcrumbItems={[
          { path: "/", label: "Home" },
          { path: "/companies", label: "Unternehmen" },
        ]}
      />

      <Card>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={showCreateForm}
          block={isMobile}
          size={isMobile ? "middle" : "large"}
        >
          Neues Unternehmen anlegen
        </Button>
      </Card>

      <Card title={<Title level={5}>Vorhandene Unternehmen</Title>}>
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} md={12}>
            <Search
              placeholder="Unternehmen suchen..."
              allowClear
              enterButton={<SearchOutlined />}
              onSearch={(value) => setSearchText(value)}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: "100%" }}
            />
          </Col>
          <Col
            xs={24}
            md={12}
            style={{ textAlign: isMobile ? "left" : "right" }}
          >
            <Dropdown overlay={sortMenu} trigger={["click"]}>
              <Button block={isMobile}>
                Sortieren <DownOutlined />
              </Button>
            </Dropdown>
          </Col>
        </Row>

        <List
          itemLayout={isMobile ? "vertical" : "horizontal"}
          loading={loading}
          dataSource={filteredCompanies}
          renderItem={(company) => (
            <List.Item
              key={company.id}
              style={{ padding: isMobile ? "12px 8px" : "16px" }}
              actions={
                isMobile
                  ? [
                      <Dropdown
                        key="more"
                        overlay={mobileActionMenu(company)}
                        trigger={["click"]}
                      >
                        <Button type="text" icon={<EllipsisOutlined />} />
                      </Dropdown>,
                    ]
                  : [
                      <Button
                        key="edit"
                        shape="circle"
                        icon={<EditOutlined />}
                        onClick={() => showEditForm(company)}
                      />,
                      <Button
                        key="delete"
                        shape="circle"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => {
                          setCompanyToDelete(company);
                          setDeleteModalVisible(true);
                        }}
                      />,
                    ]
              }
            >
              <List.Item.Meta
                avatar={
                  <div
                    style={{
                      width: isMobile ? 48 : 64,
                      height: isMobile ? 48 : 64,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      background: "#f5f5f5",
                      border: "1px solid #e8e8e8",
                      borderRadius: "4px",
                      overflow: "hidden",
                      flexShrink: 0,
                      marginRight: isMobile ? "8px" : "16px",
                    }}
                  >
                    {company.logo_filename ? (
                      <img
                        src={getCompanyLogoUrl(company.logo_filename)}
                        alt={`Logo ${company.name}`}
                        style={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          objectFit: "contain",
                        }}
                        onError={(e) => {
                          console.log(
                            "Logo konnte nicht geladen werden:",
                            company.logo_filename
                          );
                          e.target.onerror = null;
                          e.target.style.display = "none";
                        }}
                      />
                    ) : (
                      <FileImageOutlined
                        style={{
                          fontSize: isMobile ? 24 : 32,
                          color: "#bfbfbf",
                        }}
                      />
                    )}
                  </div>
                }
                title={
                  <div
                    style={{
                      fontSize: isMobile ? "16px" : "18px",
                      fontWeight: "500",
                      marginBottom: "4px",
                      wordBreak: "break-word",
                    }}
                  >
                    {company.name}
                  </div>
                }
                description={
                  <Space
                    direction="vertical"
                    size={isMobile ? "small" : "middle"}
                    style={{ width: "100%" }}
                  >
                    {renderAddress(company)}
                    {renderContact(company)}
                  </Space>
                }
              />
            </List.Item>
          )}
          pagination={
            filteredCompanies.length > 10
              ? {
                  pageSize: 10,
                  size: isMobile ? "small" : "default",
                  responsive: true,
                  showSizeChanger: !isMobile,
                  pageSizeOptions: isMobile ? ["10"] : ["10", "20", "50"],
                }
              : false
          }
        />
      </Card>

      {/* Modal für Desktop */}
      <Modal
        title={`Unternehmen ${editingCompany ? "bearbeiten" : "anlegen"}`}
        visible={isModalVisible}
        onOk={handleSubmit}
        onCancel={handleCancel}
        width={700}
        okText="Speichern"
        cancelText="Abbrechen"
        centered
        maskClosable={false}
      >
        <CompanyForm />
      </Modal>

      {/* Drawer für Mobile */}
      <Drawer
        title={`Unternehmen ${editingCompany ? "bearbeiten" : "anlegen"}`}
        placement="bottom"
        onClose={handleCancel}
        visible={isDrawerVisible}
        height="85vh"
        extra={
          <Space>
            <Button onClick={handleCancel}>Abbrechen</Button>
            <Button type="primary" onClick={handleSubmit}>
              Speichern
            </Button>
          </Space>
        }
      >
        <div style={{ height: "95%", overflow: "auto", paddingBottom: "50px" }}>
          <CompanyForm />
        </div>
      </Drawer>

      {/* Modal zur Bestätigung des Löschens */}
      <Modal
        title="Unternehmen löschen?"
        visible={deleteModalVisible}
        onOk={handleDelete}
        onCancel={() => setDeleteModalVisible(false)}
        okText="Löschen"
        cancelText="Abbrechen"
        okButtonProps={{ danger: true }}
      >
        <Text>
          Möchten Sie das Unternehmen <strong>{companyToDelete?.name}</strong>{" "}
          wirklich löschen?
        </Text>
      </Modal>
    </Space>
  );
};

export default Companies;
